import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Plus, X, ChevronDown, Download, RefreshCw, CheckCircle, AlertTriangle, XCircle, Shield, Building2, Briefcase, Brain } from 'lucide-react';
import KPICard from '../components/Dashboard/KPICard';
import LoanList from '../components/Dashboard/LoanList';
import AlertPanel from '../components/Dashboard/AlertPanel';
import AddLoanModal from '../components/Loans/AddLoanModal';
import { BreachPredictionCard, PredictionSummary } from '../components/predictions';
import { breachPredictionService } from '../services/breachPredictionService';
import { useTranslation } from '../context/LanguageContext';
import { useAuth, ROLES, ROLE_DESCRIPTIONS } from '../context/AuthContext';
import { useLoans, useAlerts, useLoanMutations } from '../hooks';
import { formatCurrency, getStatusColor } from '../utils/helper';


const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const colors = getStatusColor(status);
  const icons = { ok: CheckCircle, warning: AlertTriangle, critical: XCircle };
  const labels = { ok: t('dashboard.compliant'), warning: t('dashboard.atRisk'), critical: t('dashboard.breach') };
  const Icon = icons[status] || CheckCircle;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
      <Icon size={14} />
      {labels[status] || status}
    </span>
  );
};

const MLPredictionsPanel = ({ loans }) => {
  const { t, language } = useTranslation();

  const predictions = useMemo(() => {
    const allCovenants = loans.flatMap(loan =>
      (loan.covenants || []).map(c => ({ ...c, loan_id: loan.id, loanName: loan.borrower }))
    );

    if (allCovenants.length === 0) return { predictions: [], summary: null, highRisk: [] };

    const preds = breachPredictionService.predictBatch(allCovenants);
    const summary = breachPredictionService.generateSummary(preds);
    const highRisk = breachPredictionService.getHighRiskCovenants(preds, 0.3).slice(0, 5);

    return { predictions: preds, summary, highRisk };
  }, [loans]);

  if (!predictions.summary || predictions.predictions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">
              {language === 'fr' ? 'Prédiction ML' : 'ML Breach Prediction'}
            </h3>
            <p className="text-sm text-slate-500">
              {language === 'fr' ? 'Analyse prédictive des covenants' : 'Predictive covenant analysis'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-slate-800">
              {Math.round(predictions.summary.averageProbability * 100)}%
            </p>
            <p className="text-xs text-slate-500">
              {language === 'fr' ? 'Risque moyen' : 'Avg Risk'}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className={`text-2xl font-bold ${
              predictions.summary.portfolioRisk === 'critical' ? 'text-red-600' :
              predictions.summary.portfolioRisk === 'high' ? 'text-orange-600' :
              predictions.summary.portfolioRisk === 'medium' ? 'text-yellow-600' : 'text-green-600'
            }`}>
              {predictions.summary.portfolioRisk.toUpperCase()}
            </p>
            <p className="text-xs text-slate-500">
              {language === 'fr' ? 'Niveau risque' : 'Risk Level'}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mb-4 text-xs">
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">{predictions.summary.critical} Critical</span>
          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">{predictions.summary.high} High</span>
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{predictions.summary.low} Low</span>
        </div>

        {predictions.highRisk.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2 uppercase">
              {language === 'fr' ? 'Covenants à risque' : 'High Risk Covenants'}
            </p>
            <div className="space-y-2">
              {predictions.highRisk.map((pred, idx) => (
                <BreachPredictionCard
                  key={idx}
                  prediction={pred}
                  covenantName={pred.covenantName}
                  loanName={loans.find(l => l.id === pred.loanId)?.borrower || ''}
                  compact
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { t, language } = useTranslation();
  const { user, isAgent, isLender, isBorrower, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showAddLoanModal, setShowAddLoanModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    sector: 'all',
    country: 'all',
  });


  const roleInfo = user ? ROLE_DESCRIPTIONS[user.role] : null;
  const RoleIcon = roleInfo?.icon === 'Shield' ? Shield : roleInfo?.icon === 'Building2' ? Building2 : Briefcase;


  const userContext = user ? { role: user.role, userId: user.id } : null;


  const { loans, loading: loansLoading, refetch: refetchLoans } = useLoans({}, userContext);
  const { alerts, loading: alertsLoading } = useAlerts({ isArchived: false });
  const { createLoan } = useLoanMutations();


  const canAddLoan = hasPermission('create_loan');


  const handleAddLoan = async (newLoan) => {
    const result = await createLoan(newLoan);
    if (result.success) {
      refetchLoans();
    }
  };


  const sectors = useMemo(() => {
    return [...new Set(loans.map(l => l.sector).filter(Boolean))].sort();
  }, [loans]);

  const countries = useMemo(() => {
    return [...new Set(loans.map(l => l.country).filter(Boolean))].sort();
  }, [loans]);


  const transformedLoans = useMemo(() => {
    return loans.map(loan => {
      const covenants = loan.covenants || [];
      const hasCovenants = covenants.length > 0;
      const criticalCount = covenants.filter(c => c.status === 'critical').length;
      const warningCount = covenants.filter(c => c.status === 'warning').length;


      let status = loan.status;
      if (hasCovenants) {
        if (criticalCount > 0) status = 'critical';
        else if (warningCount > 0) status = 'warning';
        else status = 'ok';
      }

      return {
        ...loan,
        covenants,
        status,

        nextReportingDate: loan.next_reporting_date,
        startDate: loan.start_date,
        maturityDate: loan.maturity_date,
        leadArranger: loan.lead_arranger,
      };
    });
  }, [loans]);


  const filteredLoans = useMemo(() => {
    return transformedLoans.filter(loan => {
      if (filters.status !== 'all' && loan.status !== filters.status) return false;
      if (filters.sector !== 'all' && loan.sector !== filters.sector) return false;
      if (filters.country !== 'all' && loan.country !== filters.country) return false;
      return true;
    });
  }, [transformedLoans, filters]);


  const transformedAlerts = useMemo(() => {
    return alerts.map(alert => ({
      ...alert,
      loanId: alert.loan_id,
      isRead: alert.is_read === 1,
      isArchived: alert.is_archived === 1,
      createdAt: alert.created_at,
      covenant: alert.covenant_name,
      currentValue: alert.current_value,
    }));
  }, [alerts]);


  const filteredAlerts = useMemo(() => {
    const filteredLoanIds = filteredLoans.map(l => l.id);
    return transformedAlerts.filter(alert =>
      alert.loanId === null || filteredLoanIds.includes(alert.loanId)
    );
  }, [filteredLoans, transformedAlerts]);


  const dynamicKPIs = useMemo(() => {
    const totalLoans = filteredLoans.length;
    const totalCovenants = filteredLoans.reduce((sum, loan) => sum + (loan.covenants?.length || 0), 0);
    const compliantCovenants = filteredLoans.reduce((sum, loan) =>
      sum + (loan.covenants?.filter(c => c.status === 'ok').length || 0), 0
    );
    const criticalAlerts = filteredAlerts.filter(a => a.type === 'critical').length;
    const warningAlerts = filteredAlerts.filter(a => a.type === 'warning').length;
    const complianceRate = totalCovenants > 0
      ? Math.round((compliantCovenants / totalCovenants) * 100)
      : 0;
    const totalAmount = filteredLoans.reduce((sum, loan) => sum + (loan.amount || 0), 0);

    return [
      {
        id: '1',
        title: t('dashboard.totalLoans'),
        value: totalLoans.toString(),
        subtitle: formatCurrency(totalAmount),
        icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      },
      {
        id: '2',
        title: t('dashboard.activeCovenants'),
        value: totalCovenants.toString(),
        subtitle: `${compliantCovenants} ${t('dashboard.compliant').toLowerCase()}`,
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      },
      {
        id: '3',
        title: t('dashboard.alertsCount'),
        value: filteredAlerts.length.toString(),
        subtitle: `${criticalAlerts} ${t('alerts.critical').toLowerCase()}, ${warningAlerts} ${t('alerts.warning').toLowerCase()}`,
        icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
      },
      {
        id: '4',
        title: t('dashboard.complianceRate'),
        value: `${complianceRate}%`,
        subtitle: t('dashboard.portfolioOverview'),
        trendUp: complianceRate >= 80,
        icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      },
    ];
  }, [filteredLoans, filteredAlerts, t]);


  const resetFilters = () => {
    setFilters({ status: 'all', sector: 'all', country: 'all' });
  };


  const handleSelectLoan = (loan) => {
    setSelectedLoan(selectedLoan?.id === loan.id ? null : loan);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== 'all').length;


  const getCovenantProgress = (covenant) => {
    const current = covenant.current_value ?? covenant.current ?? 0;
    const threshold = covenant.threshold ?? 1;
    if (covenant.operator === '<=' || covenant.operator === '<') {
      return Math.min((current / threshold) * 100, 100);
    } else {
      return Math.min((threshold / current) * 100, 100);
    }
  };


  if (loansLoading || alertsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-slate-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }


  const getRoleWelcome = () => {
    if (isAgent()) {
      return language === 'fr' ? 'Vue Facility Agent - Gestion complète du portefeuille' : 'Facility Agent View - Full Portfolio Management';
    } else if (isLender()) {
      return language === 'fr' ? `Vue Prêteur - ${user?.company || 'Vos expositions'}` : `Lender View - ${user?.company || 'Your Exposures'}`;
    } else if (isBorrower()) {
      return language === 'fr' ? `Vue Emprunteur - ${user?.company || 'Vos prêts'}` : `Borrower View - ${user?.company || 'Your Loans'}`;
    }
    return t('dashboard.subtitle');
  };

  return (
    <div className="space-y-6">
      {}
      {user && roleInfo && (
        <div className={`rounded-xl p-4 border ${
          user.role === ROLES.AGENT ? 'bg-purple-50 border-purple-200' :
          user.role === ROLES.LENDER ? 'bg-blue-50 border-blue-200' :
          'bg-emerald-50 border-emerald-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                user.role === ROLES.AGENT ? 'bg-purple-100' :
                user.role === ROLES.LENDER ? 'bg-blue-100' :
                'bg-emerald-100'
              }`}>
                <RoleIcon className={`w-5 h-5 ${
                  user.role === ROLES.AGENT ? 'text-purple-600' :
                  user.role === ROLES.LENDER ? 'text-blue-600' :
                  'text-emerald-600'
                }`} />
              </div>
              <div>
                <p className={`font-medium ${
                  user.role === ROLES.AGENT ? 'text-purple-800' :
                  user.role === ROLES.LENDER ? 'text-blue-800' :
                  'text-emerald-800'
                }`}>
                  {language === 'fr' ? roleInfo.label : roleInfo.labelEn}
                </p>
                <p className={`text-sm ${
                  user.role === ROLES.AGENT ? 'text-purple-600' :
                  user.role === ROLES.LENDER ? 'text-blue-600' :
                  'text-emerald-600'
                }`}>
                  {language === 'fr' ? roleInfo.description : roleInfo.descriptionEn}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-sm font-medium ${
                user.role === ROLES.AGENT ? 'text-purple-800' :
                user.role === ROLES.LENDER ? 'text-blue-800' :
                'text-emerald-800'
              }`}>
                {user.firstName} {user.lastName}
              </p>
              <p className={`text-xs ${
                user.role === ROLES.AGENT ? 'text-purple-600' :
                user.role === ROLES.LENDER ? 'text-blue-600' :
                'text-emerald-600'
              }`}>
                {user.company}
              </p>
            </div>
          </div>
        </div>
      )}

      {}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('dashboard.portfolioOverview')}</h2>
          <p className="text-slate-500">
            {getRoleWelcome()}
            {activeFiltersCount > 0 && (
              <span className="ml-2 text-blue-600">
                ({filteredLoans.length} {t('loans.of')} {transformedLoans.length} {t('loans.showing').toLowerCase()})
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters || activeFiltersCount > 0
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter size={18} />
            {t('common.filter')}
            {activeFiltersCount > 0 && (
              <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
            <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          {canAddLoan && (
            <button
              onClick={() => setShowAddLoanModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              {t('loans.addLoan')}
            </button>
          )}
        </div>
      </div>

      {}
      <AddLoanModal
        isOpen={showAddLoanModal}
        onClose={() => setShowAddLoanModal(false)}
        onSubmit={handleAddLoan}
      />

      {}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex gap-4 items-end">
            {}
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('loans.status')}
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('loans.allStatuses')}</option>
                <option value="ok">{t('dashboard.compliant')}</option>
                <option value="warning">{t('dashboard.atRisk')}</option>
                <option value="critical">{t('dashboard.breach')}</option>
              </select>
            </div>

            {}
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('loans.sector')}
              </label>
              <select
                value={filters.sector}
                onChange={(e) => setFilters(prev => ({ ...prev, sector: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('loans.allSectors')}</option>
                {sectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>

            {}
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('loans.country')}
              </label>
              <select
                value={filters.country}
                onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('loans.allCountries')}</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            {}
            {activeFiltersCount > 0 && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={18} />
                {t('common.reset')}
              </button>
            )}
          </div>

          {}
          {activeFiltersCount > 0 && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
              <span className="text-sm text-slate-500">{t('common.filter')}:</span>
              {filters.status !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                  {t('loans.status')}: {filters.status === 'ok' ? t('dashboard.compliant') : filters.status === 'warning' ? t('dashboard.atRisk') : t('dashboard.breach')}
                  <button onClick={() => setFilters(prev => ({ ...prev, status: 'all' }))}>
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.sector !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                  {t('loans.sector')}: {filters.sector}
                  <button onClick={() => setFilters(prev => ({ ...prev, sector: 'all' }))}>
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.country !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                  {t('loans.country')}: {filters.country}
                  <button onClick={() => setFilters(prev => ({ ...prev, country: 'all' }))}>
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dynamicKPIs.map((kpi) => (
          <KPICard key={kpi.id} {...kpi} />
        ))}
      </div>

      {}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {}
        <div className="lg:col-span-2">
          <LoanList
            loans={filteredLoans}
            selectedLoanId={selectedLoan?.id}
            onSelectLoan={handleSelectLoan}
          />
        </div>

        {}
        <div className="space-y-6">
          <AlertPanel alerts={filteredAlerts} />

          {}
          <MLPredictionsPanel loans={filteredLoans} />
        </div>
      </div>

      {}
      {selectedLoan && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-semibold text-slate-800">{selectedLoan.borrower}</h3>
                <StatusBadge status={selectedLoan.status} />
              </div>
              <p className="text-slate-500 mt-1">
                {formatCurrency(selectedLoan.amount)} • {selectedLoan.sector} • {t('covenants.nextTest')}: {selectedLoan.nextReportingDate ? new Date(selectedLoan.nextReportingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/loans/${selectedLoan.id}`)}
                className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700"
              >
                <Download size={18} />
                {t('loans.exportReport')}
              </button>
              <button
                onClick={() => navigate(`/loans/${selectedLoan.id}?tab=covenants`)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <RefreshCw size={18} />
                {t('covenants.updateValue')}
              </button>
              <button
                onClick={() => setSelectedLoan(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(selectedLoan.covenants || []).map((covenant) => {
              const currentValue = covenant.current_value ?? covenant.current ?? 0;
              const thresholdValue = covenant.threshold ?? 0;
              return (
                <div key={covenant.id} className="border border-slate-200 rounded-lg p-4">
                  <p className="text-sm text-slate-500 mb-2">{covenant.name}</p>
                  <div className="flex items-end justify-between">
                    <p className="text-2xl font-bold text-slate-800">
                      {typeof currentValue === 'number' && currentValue >= 1000000
                        ? formatCurrency(currentValue)
                        : currentValue}
                    </p>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">
                        {covenant.operator} {typeof thresholdValue === 'number' && thresholdValue >= 1000000
                          ? formatCurrency(thresholdValue)
                          : thresholdValue}
                      </p>
                      <StatusBadge status={covenant.status} />
                    </div>
                  </div>
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        covenant.status === 'ok' ? 'bg-emerald-500' :
                        covenant.status === 'warning' ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${getCovenantProgress(covenant)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-slate-400">0</span>
                    <span className="text-xs text-slate-400">{t('covenants.threshold')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}